import { ScopeButton } from '@monorepo/shared-lib';
import { Space } from 'antd';
import LoadTaxesPic from 'assets/images/load-taxes-pic.svg';
import { StyledCard } from 'components/Custom/card';
import { CardDescSpan, CardTitleLargeSpan } from 'components/Custom/spans';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { fetchReturnTypesRequest } from 'store/returns/actions';
import { getReturnLoading } from 'store/returns/selectors';
import { getTaxRateTemplatesRequest } from 'store/tax-rates/actions';
import {
  getTaxRatesForConnection,
  getTaxRatesGettingTaxes,
} from 'store/tax-rates/selectors';
import { AddNewTaxesForm } from './add-new-taxes-form';
import { PromotionMessage } from './promotion-message';

/** Narrow side promo card width (Tax Rates tab, reconciliation intro panel). */
export const TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH = 320;

interface TaxRatesPromotionBannerProps {
  organisation?: any;
  fullWidth?: boolean;
  title?: string;
  message?: React.ReactNode;
  /** Width of the card when not fullWidth. */
  cardWidth?: number;
}

export const TaxRatesPromotionBanner: React.FC<
  TaxRatesPromotionBannerProps
> = ({
  organisation,
  fullWidth = false,
  title,
  message,
  cardWidth = TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const gettingTaxes = useSelector(getTaxRatesGettingTaxes);
  const returnLoading = useSelector(getReturnLoading);
  const taxRatesForConnectionSelector = React.useMemo(
    () => getTaxRatesForConnection(organisation?.connectionId),
    [organisation?.connectionId]
  );
  const taxRatesForConnection = useSelector(taxRatesForConnectionSelector);
  const hasExistingTaxRates = (taxRatesForConnection?.length ?? 0) > 0;
  const buttonLabel = hasExistingTaxRates
    ? t('organisation.tax-rates-promotion-add-other')
    : t('common.proceed');

  const onProceedTaxes = () => {
    dispatch(getTaxRateTemplatesRequest());
    dispatch(fetchReturnTypesRequest(organisation?.connectionId));
    const entityCountryId = organisation?.returnTypesEdit?.[0]?.countryId;
    const drawerData: DrawerData = {
      title: t('organisation.add-new-taxes'),
      type: DrawerType.ADD_NEW_TAXES,
      component: (
        <AddNewTaxesForm
          loading={gettingTaxes || returnLoading}
          connectionId={organisation?.connectionId}
          initialCountryId={entityCountryId}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  // Default message for tax rates tab
  const defaultMessage = (
    <PromotionMessage i18nKey="organisation.tax-rates-promotion-message" />
  );

  const displayMessage = message ? message : defaultMessage;

  if (fullWidth) {
    return (
      <StyledCard
        $nopadding
        fullsize
        style={{
          boxShadow: '0px 4px 10px 0px #0000000D',
          marginBottom: '20px',
          display: 'flex',
          fontSize: 14,
          fontWeight: 400,
          padding: 0,
        }}
        width="100%"
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexFlow: 'row',
            alignContent: 'end',
            padding: '24px 24px 0 0',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexFlow: 'column',
              justifyContent: 'space-between',
              padding: '0 24px 24px 24px',
            }}
          >
            {title && (
              <Space>
                <CardTitleLargeSpan style={{ marginBottom: 10 }}>
                  {title}
                </CardTitleLargeSpan>
              </Space>
            )}
            <CardDescSpan style={{ marginBottom: 16 }}>
              {displayMessage}
            </CardDescSpan>
            <ScopeButton
              type="dark-orange"
              width="180px"
              height="40px"
              fontSize="15px"
              lineheight="12px"
              onClick={onProceedTaxes}
            >
              {buttonLabel}
            </ScopeButton>
          </div>

          <img alt={t('organisation.load-tax-rates')} src={LoadTaxesPic} />
        </div>
      </StyledCard>
    );
  }

  return (
    <StyledCard
      style={{
        width: cardWidth,
        height: 'fit-content',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          alignItems: 'center',
        }}
      >
        <img
          src={LoadTaxesPic}
          alt={t('organisation.load-tax-rates')}
          style={{ width: 250, height: 'auto', display: 'block' }}
        />

        <span style={{ fontSize: 18, fontWeight: 400, lineHeight: '24px' }}>
          <PromotionMessage i18nKey="organisation.tax-rates-promotion-message" />
        </span>
        <ScopeButton
          width="180px"
          height="40px"
          fontSize="15px"
          lineheight="12px"
          type="dark-orange"
          onClick={onProceedTaxes}
          style={{ alignSelf: 'center', margin: '0 auto' }}
        >
          {buttonLabel}
        </ScopeButton>
      </div>
    </StyledCard>
  );
};
